import { useQuery } from "@tanstack/react-query";
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedDelete,
} from "../../api/authenticatedFetch";

const BASE = "/api/v1";

// --- Approval authorities (Sub-project C, Task 10) ---
//
// An approval authority is an explicit, per-user grant of approval power over
// a governed object_type, optionally capped by a max amount and/or a time
// window, and optionally scoped to a subset of the org hierarchy via clauses
// -- zero clauses is a whole-company grant. See
// backend_go cmd/api/approval_authority_handlers.go (Task 8) for the source
// of truth; modeled directly on scopeApi.js's scope-grant API (Sub-project B,
// Task 4).

// Governed object_types an approval authority may be granted over — mirrors
// backend_go's approvableObjectTypes. "budget" (the legacy, non-workflow
// object type) is deliberately excluded.
export const APPROVABLE_OBJECT_TYPES = [
  { value: "budget_creation", label: "Budget creation" },
  { value: "budget_period_version", label: "Budget period version" },
  { value: "budget_change_set", label: "Budget change set" },
  { value: "budget_action_proposal", label: "Budget action proposal" },
];

/**
 * @param {number|string} userId
 * @returns {Promise<Array<{
 *   id: number|string,
 *   objectType: string,
 *   maxAmountCents: number,
 *   maxAmount: number,
 *   startsAt: string|null,
 *   endsAt: string|null,
 *   isEnabled: boolean,
 *   clauses: Array<{dimensionCode: string, operator: string, nodeId: number, nodeLabel?: string}>,
 * }>>}
 */
export const listUserAuthorities = async (userId) => {
  const res = await authenticatedGet(`${BASE}/users/${userId}/approval-authorities`);
  return res?.approval_authorities ?? [];
};

/**
 * @param {number|string} userId
 * @param {{
 *   objectType: string,
 *   maxAmount: number,
 *   startsAt?: string,
 *   endsAt?: string,
 *   clauses: Array<{dimensionCode: string, operator: string, nodeId: number}>,
 * }} body
 */
export const createUserAuthority = async (userId, body) => {
  const res = await authenticatedPost(`${BASE}/users/${userId}/approval-authorities`, body);
  return res?.authority ?? res;
};

/**
 * @param {number|string} userId
 * @param {number|string} authId
 */
export const deleteUserAuthority = async (userId, authId) => {
  return authenticatedDelete(`${BASE}/users/${userId}/approval-authorities/${authId}`);
};

// --- react-query hooks ---

// Query key shared with the component so it can invalidate after a
// create/delete mutation (see ApprovalAuthorities.jsx).
export const authoritiesKey = (userId) => ["user-approval-authorities", userId];

export function useUserAuthorities(userId) {
  return useQuery({
    queryKey: authoritiesKey(userId),
    queryFn: () => listUserAuthorities(userId),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}
